import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';

export interface Reminder {
  clientReminderId: number;
  clientId?: number;
  assignedTo?: number;
  clientReminderType?: number;
  clientReminderDescr?: string;
  clientReminderDateTime?: string;
  clientReminderNotes?: string;
  clientReminderIsDone?: boolean;
  readAt?: string;
  clientReminderCreatedAt?: string;
  clientReminderCreatedBy?: number;
  clientReminderUpdatedAt?: string;
  clientReminderUpdatedBy?: number;
  // Related data from backend
  client?: {
    clientId: number;
    clientName?: string;
  };
  assignedToUser?: {
    id: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
  clientReminderTypeDescr?: {
    id: number;
    pr_descr?: string;
  };
  clientReminderCreatedUser?: {
    id?: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
  clientReminderUpdatedUser?: {
    id?: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
}

export interface RemindersResponse {
  records: Reminder[];
  thisWeekCount?: number;
  thisMonthCount?: number;
  open_count?: number;
  overdue_count?: number;
}

export const useReminders = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }
  
  const { data, error, isLoading, mutate } = useSWR<RemindersResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  // Extract records array from response
  const reminders = data?.records || [];

  return {
    data: reminders,
    reminders: reminders,
    total: reminders.length,
    isLoading,
    isError: error,
    mutate,
    thisWeekCount: data?.thisWeekCount || 0,
    thisMonthCount: data?.thisMonthCount || 0,
    openCount: data?.open_count || 0,
    overdueCount: data?.overdue_count || 0,
  };
};

export const useReminder = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Reminder>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    reminder: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get users for select dropdown
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    users: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((user) => ({
      value: String(user.id),
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

// Hook to get reminder types for select dropdown
export const useReminderTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-reminder-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    reminderTypes: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((type) => ({
      value: String(type.id),
      label: type.pr_descr || `Reminder Type #${type.id}`,
    })),
  };
};

